import { useEffect, useRef } from "react";
import { useLocation } from "react-router";
import { takeSignInResult } from "@/features/build/connectors/signInResult";

const KEY = "anthm.connectorSignInRetry";
const MAX_AGE_MS = 15 * 60 * 1000;

interface SavedRetry {
  path: string;
  prompt: string;
  at: number;
}

/** Before leaving to sign in to a connector (phase 6): the message to resend when I come back to this chat. */
export function rememberRetryAfterSignIn(prompt: string) {
  const saved: SavedRetry = { path: globalThis.location.pathname, prompt, at: Date.now() };
  sessionStorage.setItem(KEY, JSON.stringify(saved));
}

/** The person closed the sign-in prompt: nothing to resend later. */
export function forgetRetryAfterSignIn() {
  sessionStorage.removeItem(KEY);
}

function takeSaved(): SavedRetry | null {
  const raw = sessionStorage.getItem(KEY);
  sessionStorage.removeItem(KEY);
  if (!raw) return null;
  try {
    const saved = JSON.parse(raw) as Partial<SavedRetry>;
    if (typeof saved.path !== "string" || typeof saved.prompt !== "string" || typeof saved.at !== "number") return null;
    return saved as SavedRetry;
  } catch {
    return null;
  }
}

/**
 * Back from a connector sign-in that the chat asked for: resend the message it was for, once,
 * as soon as the chat can send. Only after a successful sign-in, in the same chat, recently.
 */
export function useRetryAfterSignIn(ready: boolean, send: (prompt: string) => void) {
  const location = useLocation();
  const done = useRef(false);
  useEffect(() => {
    if (done.current || !ready || !sessionStorage.getItem(KEY)) return;
    // The app-wide sign-in result (ConnectorSignInResult) records how it went.
    const result = takeSignInResult();
    if (!result) return;
    done.current = true;
    const saved = takeSaved();
    if (result !== "success" || !saved) return;
    if (saved.path !== location.pathname || Date.now() - saved.at > MAX_AGE_MS) return;
    send(saved.prompt);
  }, [ready, location.pathname, send]);
}
